import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card';


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        inquiryType: 'general'
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inquiryTypes = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'support', label: 'Technical Support' },
    { value: 'trading', label: 'Trading Questions' },
    { value: 'account', label: 'Account Issues' },
    { value: 'partnership', label: 'Partnership' },
    { value: 'media', label: 'Media & Press' }
  ];

  const contactMethods = [
    {
      icon: '📧',
      title: 'Email Support',
      description: 'Get help from our support team',
      contact: 'support@gainwisefx.com',
      availability: '24/7 Response',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: '📞',
      title: 'Phone Support',
      description: 'Speak directly with our team',
      contact: '+254 700 123 456',
      availability: 'Mon-Fri, 8AM-6PM EAT',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      icon: '💬',
      title: 'Live Chat',
      description: 'Instant support via live chat',
      contact: 'Available on platform',
      availability: '24/7 Available',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: '📍',
      title: 'Office Location',
      description: 'Visit our regional office',
      contact: 'Nairobi, Kenya',
      availability: 'Mon-Fri, 9AM-5PM',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  const offices = [
    {
      country: 'Kenya',
      city: 'Nairobi',
      address: 'Westlands Business District, Nairobi',
      phone: '+254 700 123 456',
      email: 'kenya@gainwisefx.com',
      flag: '🇰🇪'
    },
    {
      country: 'Uganda',
      city: 'Kampala',
      address: 'Nakasero Hill, Kampala',
      phone: '+256 700 123 456',
      email: 'uganda@gainwisefx.com',
      flag: '🇺🇬'
    },
    {
      country: 'Tanzania',
      city: 'Dar es Salaam',
      address: 'Masaki Peninsula, Dar es Salaam',
      phone: '+255 700 123 456',
      email: 'tanzania@gainwisefx.com',
      flag: '🇹🇿'
    },
    {
      country: 'Rwanda',
      city: 'Kigali',
      address: 'Kigali Heights, Kigali',
      phone: '+250 700 123 456',
      email: 'rwanda@gainwisefx.com',
      flag: '🇷🇼'
    }
  ];

  const faqs = [
    {
      question: 'How do I get started with trading?',
      answer: 'Simply create an account, verify your identity, deposit funds, and start exploring the platform. Our platform offers Smart Practice access for beginners.'
    },
    {
      question: 'What are your trading fees?',
      answer: 'We offer competitive trading fees starting from 0.1%. Fees vary based on your trading volume and account tier.'
    },
    {
      question: 'Is my money safe with GainwiseFX?',
      answer: 'Yes, we use bank-level security measures including cold storage, 2FA, and insurance coverage to protect your assets.'
    },
    {
      question: 'Do you offer mobile trading?',
      answer: 'Yes, we have mobile apps for both iOS and Android, plus our web platform is fully responsive for mobile trading.'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
                Get in
                <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  {' '}Touch
                </span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Have questions about trading, need technical support, or want to learn more about our platform? 
                We're here to help you succeed in your trading journey.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="py-16 bg-slate-800/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">How Can We Help You?</h2>
              <p className="text-slate-300">Choose the best way to reach out to our team</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {contactMethods.map((method, index) => (
                <motion.div
                  key={method.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group hover:-translate-y-2 transition-all duration-300"
                >
                  <Card className="h-full bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50 hover:border-blue-500/50 hover:shadow-xl hover:shadow-blue-500/20 transition-all duration-300">
                    <div className="p-6 text-center">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-r ${method.gradient} mb-4 text-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        {method.icon}
                      </div>
                      <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-blue-300 transition-colors">
                        {method.title}
                      </h3>
                      <p className="text-slate-300 text-sm mb-3">{method.description}</p>
                      <p className="text-blue-400 font-medium mb-2">{method.contact}</p>
                      <p className="text-slate-400 text-xs">{method.availability}</p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form & Offices */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Card className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50">
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-white mb-6">Send us a Message</h3>
                    
                    {submitStatus === 'success' && (
                      <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
                        <p className="text-green-400">Thank you! Your message has been sent successfully.</p>
                      </div>
                    )}
                    
                    {submitStatus === 'error' && (
                      <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                        <p className="text-red-400">Sorry, there was an error sending your message. Please try again.</p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Name *</label>
                          <Input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Your full name"
                            required
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-white mb-2">Email *</label>
                          <Input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="your@email.com"
                            required
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Inquiry Type</label>
                        <select
                          name="inquiryType"
                          value={formData.inquiryType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        >
                          {inquiryTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Subject *</label>
                        <Input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          placeholder="Brief description of your inquiry"
                          required
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Message *</label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="Tell us more about how we can help you..."
                          required
                          rows={5}
                          className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-300"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </div>
                </Card>
              </motion.div>

              {/* Regional Offices */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="space-y-8"
              >
                <div>
                  <h3 className="text-2xl font-bold text-white mb-6">Our East African Offices</h3>
                  <p className="text-slate-300 mb-8">
                    We're expanding across East Africa to serve you better. Visit our regional offices 
                    or contact our local teams for personalized support.
                  </p>
                </div>

                <div className="grid gap-4">
                  {offices.map((office, index) => (
                    <motion.div
                      key={office.country}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: 0.1 * index }}
                    >
                      <Card className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 backdrop-blur-sm border border-slate-700/30 hover:border-blue-500/30 hover:shadow-lg transition-all duration-300">
                        <div className="p-6">
                          <div className="flex items-center mb-3">
                            <span className="text-2xl mr-3">{office.flag}</span>
                            <div>
                              <h4 className="text-lg font-semibold text-white">{office.country}</h4>
                              <p className="text-blue-400 text-sm">{office.city}</p>
                            </div>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center text-slate-300">
                              <span className="mr-2">📍</span>
                              {office.address}
                            </div>
                            <div className="flex items-center text-slate-300">
                              <span className="mr-2">📞</span>
                              {office.phone}
                            </div>
                            <div className="flex items-center text-slate-300">
                              <span className="mr-2">📧</span>
                              {office.email}
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-slate-800/30 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Frequently Asked Questions</h2>
              <p className="text-slate-300">Find quick answers to common questions</p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-slate-700/50">
                    <div className="p-6">
                      <h4 className="text-lg font-semibold text-white mb-3">{faq.question}</h4>
                      <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-slate-300 mb-4">Still have questions?</p>
              <Button
                variant="outline"
                className="text-white border-2 border-white/20 hover:bg-white/10 hover:border-white/40 transition-all duration-300"
                onClick={() => document.querySelector('form').scrollIntoView({ behavior: 'smooth' })}
              >
                Contact Support
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
